import React, { createContext, useContext, useReducer } from 'react';

import { generateKey } from 'constants/helper';

const CONTENT_TYPE = { LIST: 'LIST', PARAGRAPH: 'PARAGRAPH' };
export const RETRY_COUNT = 3;

export const defaultDimensionName = 'New Dimension';
// Todo: similar constant for new level when functionality to create new level is added back

const defaultDimensionObj = {
  uuid: ``,
  name: defaultDimensionName, // to avoid flickering
  description: '',
  competency: null,
  contentType: CONTENT_TYPE.PARAGRAPH,
};

const defaultRoleObj = {
  uuid: ``,
  name: 'New Role', // to avoid flickering
};

const defaultListPointObject = {
  uuid: '',
  text: '',
};

const defaultDataObj = {
  uuid: `responsibility-${generateKey(5)}`,
  paragraphText: '',
  listPoints: [],
  loading: true,
};

const defaultState = {
  isLoading: true,
  hasError: false,
  roles: [
    {
      uuid: `role-12345`,
      name: 'New Role',
    },
  ],
  dimensions: [
    {
      uuid: `dim-12345`,
      name: defaultDimensionName,
      description: '',
      competency: null,
      contentType: CONTENT_TYPE.PARAGRAPH,
    },
  ],
  data: {
    'dim-12345': {
      'role-12345': {
        uuid: '',
        paragraphText: '',
        listPoints: [],
      },
    },
  },
};

export const TABLE_LAYOUT_ACTIONS = {
  ADD_ROLE: 'ADD_ROLE',
  REMOVE_ROLE_LOADING: 'REMOVE_ROLE_LOADING',
  ADD_DIMENSION: 'ADD_DIMENSION',
  REMOVE_DIMENSION_LOADING: 'REMOVE_DIMENSION_LOADING',
  MOVE_ROLE_COLUMN: 'MOVE_ROLE_COLUMN',
  MOVE_DIMENSION_ROW: 'MOVE_DIMENSION_ROW',
  DELETE_ROLE_COLUMN: 'DELETE_ROLE_COLUMN',
  DELETE_DIMENSION_ROW: 'DELETE_DIMENSION_ROW',
};

export const TABLE_DATA_ACTIONS = {
  UPDATE_ROLE_NAME: 'UPDATE_ROLE_NAME',
  UPDATE_DIMENSION_NAME: 'UPDATE_DIMENSION_NAME',
  UPDATE_DIMENSION_DESCRIPTION: 'UPDATE_DIMENSION_DESCRIPTION',
  UPDATE_DIMENSION_CONTENT_TYPE: 'UPDATE_DIMENSION_CONTENT_TYPE',
  UPDATE_DATA: 'UPDATE_DATA',
};

export const OTHER_ACTIONS = {
  INITIALIZE: 'INITIALIZE',
  SET_ERROR: 'SET_ERROR',
};

/* */

function addNewRoleToExistingData(data, roleUuid, defaultDataObj = defaultDataObj) {
  return Object.entries(data)
    .map(([dimUuid, rowObj]) => [
      dimUuid,
      {
        ...rowObj,
        [roleUuid]: defaultDataObj,
      },
    ])
    .reduce((accumulator, [dimUuid, rowObj]) => ({ ...accumulator, [dimUuid]: rowObj }), {});
}

function removeLoadingFromLevelData(data, roleUuid, updatedRoleUuid) {
  return Object.entries(data)
    .map(([dimUuid, rowObj]) => [
      dimUuid,
      {
        ...rowObj,
        [updatedRoleUuid]: { ...rowObj[roleUuid], loading: false },
        [roleUuid]: null,
      },
    ])
    .reduce((accumulator, [dimUuid, rowObj]) => ({ ...accumulator, [dimUuid]: rowObj }), {});
}

function deleteRoleFromExisitingData(data, roleUuid) {
  return addNewRoleToExistingData(data, roleUuid, null);
}

function addNewDimensionToExistingData(data, dimUuid, roles) {
  return {
    ...data,
    [dimUuid]: roles.reduce(
      (accumulator, currentRole) => ({
        ...accumulator,
        [currentRole.uuid]: defaultDataObj,
      }),
      {}
    ),
  };
}

function removeLoadingFromDimensionData(data, dimUuid, updatedDimUuid) {
  return {
    ...data,
    [updatedDimUuid]: Object.entries(data[dimUuid]).reduce(
      (previous, [roleUuid, responsibilityObj]) => ({
        ...previous,
        [roleUuid]: {
          ...responsibilityObj,
          loading: false,
        },
      }),
      {}
    ),
    [dimUuid]: null,
  };
}

function deleteDimensionFromExistingData(data, dimUuid) {
  return {
    ...data,
    [dimUuid]: null,
  };
}

const layoutReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case TABLE_LAYOUT_ACTIONS.ADD_ROLE: {
      // expect index.
      const { insertionIndex, uuid } = payload;
      const oldRoles = state.roles;

      const newRoleObj = {
        ...defaultRoleObj,
        uuid,
      };
      const newRoles = [...oldRoles.slice(0, insertionIndex), newRoleObj, ...oldRoles.slice(insertionIndex)];

      return {
        ...state,
        isLoading: true,
        roles: newRoles,
        data: addNewRoleToExistingData(state.data, newRoleObj.uuid, defaultDataObj),
      };
    }
    case TABLE_LAYOUT_ACTIONS.REMOVE_ROLE_LOADING: {
      // expect index.
      const { feUuid, beUuid, name } = payload;

      const newRoles = state.roles.map((role) => (role.uuid === feUuid ? { ...role, uuid: beUuid, name } : role));

      return {
        ...state,
        roles: newRoles,
        isLoading: false,
        data: removeLoadingFromLevelData(state.data, feUuid, beUuid),
      };
    }
    case TABLE_LAYOUT_ACTIONS.ADD_DIMENSION: {
      const { insertionIndex, uuid } = payload;
      const oldDimensions = state.dimensions;

      const newDimensionObj = {
        ...defaultDimensionObj,
        uuid,
      };

      const newDimensions = [
        ...oldDimensions.slice(0, insertionIndex),
        newDimensionObj,
        ...oldDimensions.slice(insertionIndex),
      ];

      return {
        ...state,
        isLoading: true,
        dimensions: newDimensions,
        data: addNewDimensionToExistingData(state.data, newDimensionObj.uuid, state.roles),
      };
    }
    case TABLE_LAYOUT_ACTIONS.REMOVE_DIMENSION_LOADING: {
      const { feUuid, beUuid, name } = payload;

      const newDimensions = state.dimensions.map((dim) => (dim.uuid === feUuid ? { ...dim, uuid: beUuid, name } : dim));

      return {
        ...state,
        dimensions: newDimensions,
        isLoading: false,
        data: removeLoadingFromDimensionData(state.data, feUuid, beUuid),
      };
    }
    case TABLE_LAYOUT_ACTIONS.MOVE_ROLE_COLUMN: {
      const { currentIndex, moveToIndex } = payload;

      if (moveToIndex < 0 || moveToIndex >= state.roles.length) return state;

      const roleObj = state.roles[currentIndex];
      const filteredRoles = state.roles.filter((_, index) => index !== currentIndex);

      filteredRoles.splice(moveToIndex, 0, roleObj);
      const newRoles = filteredRoles;

      return {
        ...state,
        roles: newRoles,
      };
    }
    case TABLE_LAYOUT_ACTIONS.MOVE_DIMENSION_ROW: {
      const { currentIndex, moveToIndex } = payload;

      if (moveToIndex < 0 || moveToIndex >= state.dimensions.length) return state;

      const dimensionObj = state.dimensions[currentIndex];
      const filteredDimensions = state.dimensions.filter((dObj, index) => index !== currentIndex);

      filteredDimensions.splice(moveToIndex, 0, dimensionObj);
      const newDimensions = filteredDimensions;

      return {
        ...state,
        dimensions: newDimensions,
      };
    }
    case TABLE_LAYOUT_ACTIONS.DELETE_ROLE_COLUMN: {
      const { roleUuid } = payload;

      return {
        ...state,
        roles: state.roles.filter((role) => role.uuid !== roleUuid),
        data: deleteRoleFromExisitingData(state.data, roleUuid),
      };
    }
    case TABLE_LAYOUT_ACTIONS.DELETE_DIMENSION_ROW: {
      const { dimensionUuid } = payload;

      return {
        ...state,
        dimensions: state.dimensions.filter((dim) => dim.uuid !== dimensionUuid),
        data: deleteDimensionFromExistingData(state.data, dimensionUuid),
      };
    }
    default:
      return state;
  }
};

const dataReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case TABLE_DATA_ACTIONS.UPDATE_ROLE_NAME: {
      const { name, uuid } = payload;

      return {
        ...state,
        roles: state.roles.map((roleObj) =>
          roleObj.uuid === uuid
            ? {
                ...roleObj,
                name,
              }
            : roleObj
        ),
      };
    }
    case TABLE_DATA_ACTIONS.UPDATE_DIMENSION_NAME: {
      const { name, uuid, competency } = payload;
      return {
        ...state,
        dimensions: state.dimensions.map((dimObj) =>
          dimObj.uuid === uuid
            ? {
                ...dimObj,
                name: competency ? '' : name,
                competency: competency || null,
              }
            : dimObj
        ),
      };
    }
    case TABLE_DATA_ACTIONS.UPDATE_DIMENSION_DESCRIPTION: {
      const { description, uuid } = payload;

      return {
        ...state,
        dimensions: state.dimensions.map((dimObj) =>
          dimObj.uuid === uuid
            ? {
                ...dimObj,
                description,
              }
            : dimObj
        ),
      };
    }
    case TABLE_DATA_ACTIONS.UPDATE_DATA: {
      const { roleUuid, dimensionUuid, data } = payload;

      return {
        ...state,
        data: {
          ...state.data,
          [dimensionUuid]: {
            ...state.data[dimensionUuid],
            [roleUuid]: {
              ...state.data[dimensionUuid][roleUuid],
              ...data,
            },
          },
        },
      };
    }
    case TABLE_DATA_ACTIONS.UPDATE_DIMENSION_CONTENT_TYPE: {
      const { contentType, uuid } = payload;

      return {
        ...state,
        dimensions: state.dimensions.map((dimObj) =>
          dimObj.uuid === uuid
            ? {
                ...dimObj,
                contentType,
              }
            : dimObj
        ),
      };
    }
    default:
      return state;
  }
};

const otherActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case OTHER_ACTIONS.INITIALIZE: {
      const { dimensions, levels, responsibilities } = payload;

      const responsibilitiesObj = responsibilities.reduce(
        (previous, current) => ({
          ...previous,
          [current.jobDimensionId]: {
            ...previous[current.jobDimensionId],
            [current.jobGradeId]: {
              ...defaultDataObj,
              loading: false,
              uuid: current.uuid,
              paragraphText: current.text,
              listPoints: current.jobResponsibilityBullets.map((bulletPoint) => ({
                uuid: bulletPoint.uuid,
                text: bulletPoint.text,
              })),
            },
          },
        }),
        {}
      );

      const returnedState = {
        dimensions: dimensions.map((dim) => {
          const { uuid, name, description, competency, isBullet } = dim;

          return {
            uuid,
            name,
            description,
            competency,
            contentType: isBullet ? CONTENT_TYPE.LIST : CONTENT_TYPE.PARAGRAPH,
          };
        }),
        roles: levels.map((level) => {
          const { uuid, name } = level;

          return {
            uuid,
            name,
          };
        }),
        data: dimensions.reduce(
          (previous, current) => ({
            ...previous,
            [current.uuid]: levels.reduce(
              (prevLevels, currentLevel) => ({
                ...prevLevels,
                [currentLevel.uuid]: responsibilitiesObj?.[current.uuid]?.[currentLevel.uuid] ?? {
                  ...defaultDataObj,
                  loading: false,
                },
              }),
              {}
            ),
          }),
          {}
        ),
      };
      return returnedState;
    }

    case OTHER_ACTIONS.SET_ERROR: {
      return {
        ...state,
        hasError: true,
        isLoading: true,
      };
    }

    default:
      return state;
  }
};

const combinedReducer = (state, action) => {
  const { type } = action;

  if (Object.values(TABLE_LAYOUT_ACTIONS).includes(type)) {
    return layoutReducer(state, action);
  }
  if (Object.values(TABLE_DATA_ACTIONS).includes(type)) {
    return dataReducer(state, action);
  }
  return otherActionsReducer(state, action);
};

const GrowthTableContext = createContext(defaultState);

export const GrowthTableContextProvider = ({ children }) => {
  const [dataObj, reducerDispatch] = useReducer(combinedReducer, defaultState);
  return (
    <GrowthTableContext.Provider
      value={{
        state: dataObj,
        tableReducerDispatch: reducerDispatch,
      }}
    >
      {children}
    </GrowthTableContext.Provider>
  );
};

export const useGrowthTableContext = () => useContext(GrowthTableContext);
